import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Robot, ArrowsClockwise, X, Check, Info } from '@phosphor-icons/react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONFIDENCE_CONFIG = {
  eleve: { color: 'bg-emerald-50 border-emerald-200 text-emerald-800', icon: Robot, label: 'Confiance élevée' },
  moyen: { color: 'bg-amber-50 border-amber-200 text-amber-800', icon: Robot, label: 'Proposition partielle' },
  faible: { color: 'bg-slate-50 border-slate-200 text-slate-700', icon: Info, label: 'Dossier insuffisant' },
};

export default function DPGFProactiveBanner({ chantierId, dpgfLots, onImport }) {
  const [analyse, setAnalyse] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLots, setSelectedLots] = useState([]);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (dpgfLots && dpgfLots.length < 3) fetchProposition();
  }, [chantierId]);

  const fetchProposition = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/conception/proposition-dpgf/${chantierId}`, { withCredentials: true });
      if (data.has_proposition) {
        setAnalyse(data.analyse);
        setSelectedLots(
          (data.analyse.lots_identifies || [])
            .filter(l => l.certitude_lot === 'haute')
            .map(l => l.nom)
        );
      }
    } catch { /* ignore */ }
  };

  const triggerAnalysis = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/conception/analyser-dossier-dpgf`, { chantier_id: chantierId }, { withCredentials: true });
      setAnalyse(data);
      setSelectedLots(
        (data.lots_identifies || [])
          .filter(l => l.certitude_lot === 'haute')
          .map(l => l.nom)
      );
      toast.success('Analyse DPGF terminée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur analyse IA');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedLots.length) return toast.error('Sélectionnez au moins un lot');
    setImporting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/conception/importer-proposition`, {
        chantier_id: chantierId,
        lots_selectionnes: selectedLots,
      }, { withCredentials: true });
      toast.success(data.message);
      setShowModal(false);
      setDismissed(true);
      if (onImport) onImport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  const toggleLot = (nom) => {
    setSelectedLots(prev => prev.includes(nom) ? prev.filter(n => n !== nom) : [...prev, nom]);
  };

  if (dismissed) return null;
  if (!analyse && dpgfLots && dpgfLots.length >= 3) return null;

  const conf = analyse ? CONFIDENCE_CONFIG[analyse.niveau_confiance] || CONFIDENCE_CONFIG.faible : null;

  return (
    <>
      {/* Banner */}
      <div className={`mb-4 p-3 border flex items-center justify-between ${conf ? conf.color : 'bg-[#F5F5F3] border-[#E0E0DB] text-[#2C2C2C]'}`} data-testid="dpgf-ai-banner">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {loading ? (
            <ArrowsClockwise size={18} className="animate-spin text-[#6366F1] flex-shrink-0" />
          ) : (
            <Robot size={18} className="text-[#6366F1] flex-shrink-0" />
          )}
          <div className="min-w-0">
            {loading ? (
              <p className="text-sm">Analyse du dossier en cours...</p>
            ) : analyse ? (
              analyse.niveau_confiance === 'faible' ? (
                <p className="text-sm">Dossier insuffisant pour proposer une DPGF. Complétez la description du projet.</p>
              ) : (
                <p className="text-sm">
                  L'IA peut proposer une base de <strong>{analyse.nb_lots_proposes}</strong> lots et <strong>{analyse.nb_postes_proposes}</strong> postes.
                </p>
              )
            ) : (
              <p className="text-sm">La DPGF est vide. L'IA peut analyser votre dossier et proposer une base.</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          {!loading && analyse && analyse.niveau_confiance !== 'faible' && (
            <Button size="sm" onClick={() => setShowModal(true)} className="h-7 bg-[#6366F1] hover:bg-[#4F46E5] text-xs" data-testid="dpgf-ai-voir-proposition">
              Voir la proposition
            </Button>
          )}
          {!loading && !analyse && (
            <Button size="sm" onClick={triggerAnalysis} className="h-7 bg-[#6366F1] hover:bg-[#4F46E5] text-xs" data-testid="dpgf-ai-analyser">
              Analyser le dossier
            </Button>
          )}
          {!loading && analyse && (
            <Button size="sm" variant="ghost" onClick={triggerAnalysis} className="h-7 text-xs text-[#6366F1]" data-testid="dpgf-ai-reanalyser">
              <ArrowsClockwise size={14} className="mr-1" /> Réanalyser
            </Button>
          )}
          <button onClick={() => setDismissed(true)} className="text-[#9A9A9A] hover:text-[#2C2C2C]" data-testid="dpgf-ai-dismiss">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && analyse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="bg-white w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="dpgf-ai-modal">
            <div className="p-4 border-b border-[#E0E0DB] bg-[#F5F5F3]">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-[#2C2C2C] flex items-center gap-2">
                  <Robot size={20} className="text-[#6366F1]" /> Proposition IA — DPGF
                </h3>
                <button onClick={() => setShowModal(false)} className="text-[#6B6B6B] hover:text-[#2C2C2C]"><X size={20} /></button>
              </div>
              <div className={`inline-flex items-center gap-1 text-xs px-2 py-1 ${conf?.color || ''}`}>
                {conf?.label} — {analyse.raison_confiance}
              </div>
              <p className="text-xs text-[#9A9A9A] mt-2">Point de départ — vérifiez chaque poste</p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {(analyse.lots_identifies || []).map((lot, i) => (
                <div key={i} className="border border-[#E0E0DB]">
                  <label className="flex items-center gap-3 p-3 bg-[#F5F5F3] cursor-pointer hover:bg-[#EBEBEA]">
                    <input
                      type="checkbox"
                      checked={selectedLots.includes(lot.nom)}
                      onChange={() => toggleLot(lot.nom)}
                      className="accent-[#6366F1]"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-sm">{lot.nom}</span>
                      <span className="text-[10px] ml-2 text-[#9A9A9A]">({lot.certitude_lot})</span>
                    </div>
                    <span className="text-xs text-[#6B6B6B]">{lot.postes?.length || 0} postes</span>
                  </label>
                  {lot.postes && lot.postes.length > 0 && (
                    <div className="px-3 pb-2 pt-1">
                      {lot.postes.map((p, j) => (
                        <div key={j} className="flex items-center justify-between py-1 text-xs border-b border-[#E0E0DB] last:border-0">
                          <span className="text-[#2C2C2C]">{p.designation}</span>
                          <span className="text-[#9A9A9A] ml-2">{p.unite}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {analyse.elements_manquants && analyse.elements_manquants.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-700">
                  <p className="font-medium mb-1">Éléments manquants pour affiner :</p>
                  <ul className="list-disc list-inside">{analyse.elements_manquants.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[#E0E0DB] bg-[#F5F5F3]">
              <p className="text-[10px] text-[#9A9A9A] mb-3">Les quantités et prix ne sont pas générés — c'est votre expertise qui prime.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowModal(false)} className="text-xs h-8">Annuler</Button>
                <Button onClick={handleImport} disabled={importing || !selectedLots.length} className="bg-[#6366F1] hover:bg-[#4F46E5] text-xs h-8" data-testid="dpgf-ai-import">
                  {importing ? <ArrowsClockwise size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
                  Importer {selectedLots.length} lot{selectedLots.length > 1 ? 's' : ''} sélectionné{selectedLots.length > 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
